import React, { useRef, useState } from "react";
import JWPlayer from '@jwplayer/jwplayer-react';
import { Video } from "../../models/interfaces/video";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { to6391 } from "../../models/enumerations/language-code-mapping";
import { createLogger } from "../../utilities/logger";

const log = createLogger("JWVideoPlayer");

const COMPONENT_CLASS = "c-jw-video-player";

export interface JWVideoPlayerProps {
    instructions?: string[];
    video?: Video;
    aspectRatio?: string; // e.g. "1:1", "16:9", "4:3"
    autoStartViewable?: boolean;
    showThumbnail?: boolean;
    thumbnailUrl?: string;
}

const JWVideoPlayer: React.FC<JWVideoPlayerProps> = (props) => {
    const {
        video,
        instructions,
        aspectRatio = "1:1",
        autoStartViewable = true,
        showThumbnail = false,
        thumbnailUrl,
    } = props;
    const playerRef = useRef<HTMLDivElement>(null);
    const [isInitialAudioTrackLoaded, setIsInitialAudioTrackLoaded] = useState<boolean>(false);
    const { t } = useTranslation();
    const resolvedPoster =
        !autoStartViewable && showThumbnail && thumbnailUrl
            ? thumbnailUrl
            : undefined;

    const handlePlayerReady = () => {
        if (!playerRef.current) return;

        const player = window.jwplayer(playerRef.current.id);
        player.on('meta', () => {
            if (!isInitialAudioTrackLoaded) {
                const audioTracks = player.getAudioTracks();

                // Get the base 2-letter language code (lowercase) from i18n
                // Handles cases like 'en-US' -> 'en' 
                // add this to end if needed .split('-')[0];
                const currentLanguage = i18n.language.toLowerCase()
                // Find matching audio track
                const matchingTrack = audioTracks.find((track: any) => {
                    // Convert track's 3-letter code to 2-letter code
                    const trackLanguage = to6391(track.language).toLowerCase();
                    return trackLanguage === currentLanguage;
                });


                if (matchingTrack) {
                    try {
                        setIsInitialAudioTrackLoaded(true);
                        player.setCurrentAudioTrack(matchingTrack.hlsjsIndex);
                    } catch (error) {
                        log.error('Error setting audio track', {
                            exception: error,
                        });
                    }
                }
            }
        });

        // Cleanup function
        return () => {
            player.off('meta');
        };
    };

    return (
        <div className={COMPONENT_CLASS} data-testid="jwplayer-video">
            {video?.videoId && (
                <div className={`${COMPONENT_CLASS}__container`}>
                    <JWPlayer
                        ref={playerRef}
                        className="jw-player-container"
                        library={`https://cdn.jwplayer.com/libraries/${process.env.REACT_APP_JW_PLAYER_ID}.js`}
                        playerId={process.env.REACT_APP_JW_PLAYER_ID ?? ""}
                        playlist={[{
                            mediaId: video.videoId,
                            image: resolvedPoster,
                            file: `https://cdn.jwplayer.com/manifests/${video.videoId}.m3u8`,
                            sources: [
                                {
                                    file: `https://cdn.jwplayer.com/manifests/${video.videoId}.m3u8`,
                                    type: "hls"
                                },
                                // Include other sources from your config
                            ],
                        }]}
                        aspectratio={aspectRatio}
                        stretching="fill"
                        autostart={autoStartViewable ? "viewable" : false}
                        controls={true}
                        onReady={handlePlayerReady}
                    />
                </div>
            )}

            {instructions && instructions?.length >= 1 && (
                <div className={`${COMPONENT_CLASS}__video-instruction scroll-container`}>
                    <ul>
                        {instructions.map((instruction, index) => (
                            <li key={`instruction-${index}`}>{t(instruction)}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default JWVideoPlayer;